/**
 * Create Tenant Modal
 * 
 * Modal for creating a new tenant with:
 * - Basic info: name, owner email
 * - Storage quota configuration
 */

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { formResolver } from '@/lib/form-resolver';
import { z } from 'zod';
import { DiscardChangesDialog } from '@vanguard-atlas/shared';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Button,
  Separator,
} from '@/components/ui';
import { LabeledInput } from '@/components/ui/form-wrappers';
import { useCreateTenant } from '../hooks';
import { Building2 } from 'lucide-react';

interface CreateTenantModalProps {
  onClose: () => void;
}

const createTenantSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters').max(100, 'Name must be less than 100 characters'),
  owner_email: z.string().email('Invalid email address'),
  max_storage_gb: z.coerce.number().min(1, 'Minimum 1 GB').max(1000, 'Maximum 1000 GB'),
  max_agents: z.coerce.number().min(1, 'Minimum 1 agent').max(100, 'Maximum 100 agents'),
  max_members: z.coerce.number().min(1, 'Minimum 1 member').max(1000, 'Maximum 1000 members'),
});

type CreateTenantFormData = z.infer<typeof createTenantSchema>;

export function CreateTenantModal({ onClose }: CreateTenantModalProps) {
  const { mutate: createTenant, isPending, error } = useCreateTenant();

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<CreateTenantFormData>({
    resolver: formResolver(createTenantSchema),
    defaultValues: {
      max_storage_gb: 50,
      max_agents: 10,
      max_members: 50,
    },
  });

  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);
  const discardLabels = {
    title: 'Discard changes?',
    message: 'You have unsaved changes. If you close now, they will be lost.',
    confirm: 'Discard',
    cancel: 'Keep editing',
  };
  const handleClose = () => {
    if (isDirty) {
      setConfirmDiscardOpen(true);
      return;
    }
    onClose();
  };

  const onSubmit = (data: CreateTenantFormData) => {
    createTenant(data, { onSuccess: onClose });
  };

  return (
    <Dialog open onOpenChange={handleClose}>
      <DialogContent
        className="max-w-lg"
        dirty={isDirty}
        discardTitle={discardLabels.title}
        discardMessage={discardLabels.message}
        discardConfirmLabel={discardLabels.confirm}
        discardCancelLabel={discardLabels.cancel}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Create New Tenant
          </DialogTitle>
          <DialogDescription>
            Create a new organization workspace with storage and resource quotas.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Error Display */}
          {error && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-md p-3">
              <p className="text-sm text-destructive">
                {error instanceof Error ? error.message : 'Failed to create tenant'}
              </p>
            </div>
          )}

          {/* Basic Info */}
          <LabeledInput
            label="Tenant Name"
            placeholder="Acme Corp"
            {...register('name')}
            error={errors.name?.message}
            required
          />

          <LabeledInput
            label="Owner Email"
            type="email"
            placeholder="admin@acme.com"
            hint="The owner will receive an invitation email"
            {...register('owner_email')}
            error={errors.owner_email?.message}
            required
          />

          <Separator className="my-4" />

          <h4 className="font-medium text-sm text-muted-foreground">Resource Quotas</h4>

          <div className="grid grid-cols-3 gap-4">
            <LabeledInput
              label="Max Storage (GB)"
              type="number"
              {...register('max_storage_gb')}
              error={errors.max_storage_gb?.message}
            />
            <LabeledInput
              label="Max Agents"
              type="number"
              {...register('max_agents')}
              error={errors.max_agents?.message}
            />
            <LabeledInput
              label="Max Members"
              type="number"
              {...register('max_members')}
              error={errors.max_members?.message}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? 'Creating...' : 'Create Tenant'}
            </Button>
          </div>
        </form>
      </DialogContent>

      {/* ADR-114 enmienda 1: confirmación de descarte */}
      <DiscardChangesDialog
        open={confirmDiscardOpen}
        onDiscard={() => {
          setConfirmDiscardOpen(false);
          onClose();
        }}
        onCancel={() => setConfirmDiscardOpen(false)}
        title={discardLabels.title}
        message={discardLabels.message}
        confirmLabel={discardLabels.confirm}
        cancelLabel={discardLabels.cancel}
      />
    </Dialog>
  );
}
